import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { acceptAttr } from '@/lib/fieldValidation'
import { cn } from '@/lib/utils'
import type { ApiFieldDef } from '@/types/inventory'

/** Radix Select forbids an empty item value, so "not set" needs a sentinel. */
const NONE = '__none__'

interface FieldValueInputProps {
  def: ApiFieldDef
  value: unknown
  invalid?: boolean
  onChange: (value: unknown) => void
  /**
   * Reports a problem only this widget can detect. File SIZE is the reason this
   * exists: the stored value is just a name, so neither the shared validator nor
   * the backend can see how big the file was — but the picker can.
   */
  onLocalError?: (message: string | null) => void
  /**
   * Human label for one `enum` option. Options are stored as machine values —
   * `storageCondition` holds `VAPOR_PHASE_LN2` because put-away validation
   * compares it against a database enum — and a dropdown of shouty identifiers
   * is not something a lab tech should have to read.
   */
  optionLabel?: (option: string) => string
}

/**
 * The value editor for one field, chosen by the def's `dataType` and shaped by
 * its `validation` rules — a `number` def gets a numeric input bounded by its
 * min/max, an `enum` def gets a dropdown of its options, and so on.
 *
 * Before this existed, everything except boolean/number/date fell through to a
 * free-text box, so a "select" field was untyped prose and a "file" field
 * couldn't take a file.
 */
export function FieldValueInput({
  def,
  value,
  invalid,
  onChange,
  onLocalError,
  optionLabel,
}: FieldValueInputProps) {
  const rules = def.validation ?? {}
  const ring = invalid ? 'border-destructive focus-visible:ring-destructive' : ''

  if (def.dataType === 'boolean') {
    return <Switch checked={!!value} onCheckedChange={(v) => onChange(v)} />
  }

  if (def.dataType === 'enum') {
    const options = def.options ?? []
    // A def with no options is incomplete. Say so rather than rendering an empty
    // dropdown the user can click but never pick anything from.
    if (options.length === 0) {
      return (
        <span className='text-xs italic text-muted-foreground'>
          No options configured for this field
        </span>
      )
    }
    const current = value == null || value === '' ? NONE : String(value)
    // A value saved before its option was removed (or renamed) stays listed, so
    // the dropdown shows it instead of going blank and can still be changed.
    const shown = current !== NONE && !options.includes(current) ? [...options, current] : options
    return (
      // '' shows the placeholder: a required field has no "Not set" item to select.
      <Select
        value={current === NONE && rules.required ? '' : current}
        onValueChange={(v) => onChange(v === NONE ? null : v)}
      >
        <SelectTrigger className={cn('h-8 w-56', ring)}>
          <SelectValue placeholder='Select…' />
        </SelectTrigger>
        <SelectContent>
          {!rules.required && (
            <SelectItem value={NONE}>
              <span className='text-muted-foreground'>Not set</span>
            </SelectItem>
          )}
          {shown.map((o) => (
            <SelectItem key={o} value={o}>
              {optionLabel ? optionLabel(o) : o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  if (def.dataType === 'file') {
    const name = value == null ? '' : String(value)
    return (
      <div className='flex w-48 flex-col items-end gap-1'>
        <Input
          type='file'
          accept={acceptAttr(def)}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (!file) {
              onLocalError?.(null)
              onChange(null)
              return
            }
            // Size is checked here or nowhere — only the name gets persisted.
            const limit = rules.maxSizeMb
            if (limit !== undefined && file.size > limit * 1024 * 1024) {
              const actual = (file.size / (1024 * 1024)).toFixed(1)
              e.target.value = '' // don't leave a rejected file showing as picked
              // Order matters: onChange re-validates and clears the field's error,
              // so the size message has to be set after it, not before.
              onChange(null)
              onLocalError?.(`${def.label} must be ${limit} MB or smaller (that file is ${actual} MB)`)
              return
            }
            onChange(file.name)
            onLocalError?.(null)
          }}
          className={cn('h-8 w-full text-xs file:mr-2 file:text-xs', ring)}
        />
        {name && <span className='truncate text-xs text-gray-600'>{name}</span>}
        {/* Honest about the gap: there is no upload endpoint in the stack yet, so
            only the file's name is recorded against the material. */}
        <span className='text-[10px] italic text-muted-foreground'>
          Name only — file upload isn’t available yet
        </span>
      </div>
    )
  }

  if (def.dataType === 'number') {
    return (
      <Input
        type='number'
        inputMode='decimal'
        value={value == null ? '' : String(value)}
        min={rules.min}
        max={rules.max}
        step={rules.step ?? (rules.integer ? 1 : undefined)}
        onChange={(e) => onChange(e.target.value)}
        className={cn('h-8 w-48', ring)}
      />
    )
  }

  if (def.dataType === 'date') {
    return (
      <Input
        type='date'
        value={value == null ? '' : String(value).slice(0, 10)}
        min={rules.minDate?.slice(0, 10)}
        max={rules.maxDate?.slice(0, 10)}
        onChange={(e) => onChange(e.target.value)}
        className={cn('h-8 w-48', ring)}
      />
    )
  }

  return (
    <Input
      type='text'
      value={value == null ? '' : String(value)}
      maxLength={rules.maxLength}
      onChange={(e) => onChange(e.target.value)}
      className={cn('h-8 w-48', ring)}
    />
  )
}
